import { useEffect, useState } from 'react';

const CACHE_KEY = 'stpk_geo_country';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — plenty for a session

/**
 * Returns the viewer's { countryName, countryCode } based on their public IP.
 *
 * Uses the free ipapi.co endpoint (no API key, ~30k requests/day on the
 * anonymous tier) and caches the result in localStorage for 24h so repeat
 * form opens don't hit the network. All failures swallow silently and return
 * `{ countryName: null, countryCode: null }` so callers can fall back to
 * their existing default (United States).
 */
export default function useGeoCountry() {
  const [geo, setGeo] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return { countryName: null, countryCode: null };
      const { ts, countryName, countryCode } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL_MS) return { countryName: null, countryCode: null };
      return { countryName, countryCode };
    } catch (_) {
      return { countryName: null, countryCode: null };
    }
  });

  useEffect(() => {
    if (geo.countryCode) return undefined; // already cached and fresh
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) return;
        const data = await res.json();
        const countryName = data.country_name || null;
        const countryCode = data.country_code || null;
        if (cancelled || !countryName || !countryCode) return;
        setGeo({ countryName, countryCode });
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), countryName, countryCode }));
        } catch (_) { /* storage disabled */ }
      } catch (_) { /* network / CORS failure — fall back silently */ }
    })();
    return () => { cancelled = true; };
  }, [geo.countryCode]);

  return geo;
}
